import {
    employeeSessionQueryKey,
    installEmployeeSessionRecovery,
} from '@features/employee-session';
import { UnauthenticatedError } from '@lib/api';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { sessionKeysToReset } from './session-reset';

import type { QueryKey } from '@tanstack/react-query';

/**
 * TanStack Query is the single home for server data (mirrors the portal's
 * `shared/providers`).
 *
 * ## Why an expired session is handled here and not per screen
 *
 * `DashboardLayout` is the auth gate, but it only ever watches the **operator**
 * query. That is enough at load and wrong afterwards: Atelier sessions expire on
 * their own schedule, so a tab left open overnight has a valid cached operator
 * and an API that now 401s everything. The flags screen would render its error
 * panel — "Couldn't load this from the API · No valid Atelier session" — with a
 * Retry button that cannot ever work, and the employee would have no way to
 * reach the sign-in route short of guessing the URL.
 *
 * Discarding the cached operator on any `UnauthenticatedError` closes that loop:
 * the gate re-asks `/auth/me`, gets its own 401, and redirects. Doing it in the
 * cache rather than in each screen means a feature added later inherits the
 * behaviour instead of having to remember it — the same reason the API registers
 * its session guard globally rather than per controller.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
    const [client] = useState(() => {
        const recheckSession = (error: Error, failedQueryKey?: QueryKey) => {
            if (!(error instanceof UnauthenticatedError)) return;
            for (const key of sessionKeysToReset(failedQueryKey)) {
                // `reset`, not `invalidate`.
                //
                // Invalidating marks the query stale but **keeps its last
                // successful data**, and that data is what `/sign-in` reads to
                // decide whether there is anything to sign in to. So the gate would
                // see the error and send the employee to `/sign-in`, `/sign-in`
                // would see the stale operator and send them straight back, and the
                // two would trade the route until React gave up with "Maximum
                // update depth exceeded" and rendered a blank page. Resetting
                // clears the data and the error together, which is what makes the
                // signed-out state actually representable — and, for the
                // employee-session and refusals caches, what keeps a lapsed
                // session's permissions and PII from answering the next read.
                //
                // `void`: this runs inside a cache callback, and a rejection here
                // must not become an unhandled rejection on a path whose whole job
                // is recovering from a failure.
                void queryClient.resetQueries({ queryKey: key });
            }
        };

        const queryClient: QueryClient = new QueryClient({
            queryCache: new QueryCache({
                onError: (error, query) => {
                    recheckSession(error, query.queryKey);
                },
            }),
            // Writes need this too: a 401 on a toggle is the same signal as a
            // 401 on a read, and a screen that only reads on mount would
            // otherwise never notice the session had gone. A mutation has no
            // query key of its own to exclude, so every sensitive cache resets.
            mutationCache: new MutationCache({ onError: (error) => recheckSession(error) }),
            defaultOptions: {
                queries: { staleTime: 30_000, refetchOnWindowFocus: false },
            },
        });

        // The employee-pool session (INI-0313) is repaired, not discarded: a
        // request answered `EMPLOYEE_SESSION_REQUIRED` refreshes it once and
        // retries once, and the refreshed session lands in the cache the nav
        // reads, without another round trip.
        installEmployeeSessionRecovery((session) => {
            queryClient.setQueryData(employeeSessionQueryKey, session);
        });

        return queryClient;
    });

    return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
